// −/+ buttons (AD-11): taps are collected for about 300 ms and sent as one absolute target value, never as steps.
import { findCommand, orioneCommandCatalog, type CommandCatalog } from '@brewcore/protocol';
import type { MachineStore } from './store.js';
import type { CommandOutcome } from './commands.js';

export const STEP_DEBOUNCE_MS = 300;

export interface StepOptions {
  /** from 0 ("aus", "nie") the first + starts here instead of one step up (ZERO_START of the local app) */
  zeroStart?: number;
  /**
   * the lowest value − reaches, above the catalog's: a value whose 0 is "aus" behind its own switch (keeping
   * warm) never steps to 0 (Dominik 23.10.2026: „bei standby warm halten muss man solange auf minus druecken bis man
   * bei aus landet“). − never lifts a value that is already below it (one kept from before), and a typed value keeps
   * only the catalog's limits.
   */
  min?: number;
  /** further values that go into the same command, computed when it is sent (e.g. the quick choice follows a target) */
  extra?: (value: number) => Record<string, boolean | number | string>;
}

export class Stepper {
  private target = new Map<string, number>();
  private options = new Map<string, StepOptions>();
  private outcomes = new Map<string, CommandOutcome>();
  private timers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly listeners = new Set<() => void>();

  constructor(
    private readonly store: MachineStore,
    private readonly send: (params: Record<string, boolean | number | string>) => Promise<CommandOutcome>,
    private readonly catalog: CommandCatalog = orioneCommandCatalog,
    private readonly debounceMs = STEP_DEBOUNCE_MS,
  ) {}

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** The value the stepper shows while collecting taps (null if not collecting). */
  pending(param: string): number | null {
    return this.target.get(param) ?? null;
  }

  /** Answer to the last value this stepper sent for param. */
  outcome(param: string): CommandOutcome | null {
    return this.outcomes.get(param) ?? null;
  }

  step(param: string, direction: 1 | -1, options: StepOptions = {}): void {
    const c = findCommand(this.catalog, param);
    const step = c?.step ?? 1;
    const base = this.target.get(param) ?? Number(this.store.value(param) ?? c?.default ?? 0);
    let next =
      base === 0 && direction === 1 && options.zeroStart !== undefined
        ? options.zeroStart
        : Math.round((base + direction * step) / step) * step;
    // − stops at the screen's lower limit, or where it already is below it: a tap on − never raises the value
    if (direction === -1 && options.min !== undefined) next = Math.max(Math.min(options.min, base), next);
    this.collect(param, next, options);
  }

  /** A typed value: as precise as given, within the limits, sent like a tap. */
  set(param: string, value: number, options: StepOptions = {}): void {
    this.collect(param, value, options);
  }

  private collect(param: string, value: number, options: StepOptions): void {
    const c = findCommand(this.catalog, param);
    let next = value;
    if (c?.min !== undefined) next = Math.max(c.min, next);
    if (c?.max !== undefined) next = Math.min(c.max, next);
    next = Number(next.toFixed(6));
    this.target.set(param, next);
    this.options.set(param, options);
    const old = this.timers.get(param);
    if (old) clearTimeout(old);
    this.timers.set(
      param,
      setTimeout(() => void this.commit(param), this.debounceMs),
    );
    this.emit();
  }

  /** Sends the collected value of param now (the timer, or flush()). */
  private commit(param: string): Promise<void> {
    const timer = this.timers.get(param);
    if (timer) clearTimeout(timer);
    this.timers.delete(param);
    const value = this.target.get(param);
    const extra = this.options.get(param)?.extra;
    this.target.delete(param);
    let sent: Promise<void> = Promise.resolve();
    if (value !== undefined) {
      this.outcomes.delete(param);
      sent = this.send({ ...(extra ? extra(value) : {}), [param]: value }).then((o) => {
        this.outcomes.set(param, o);
        this.emit();
      });
    }
    this.emit();
    return sent;
  }

  /**
   * Sends every value still being collected right away and waits for the answers: before another bean is chosen, so the
   * taps go to the bean they were made for (flushSteps() of the local app, D-B02 of APP-PARITY.md "Detail-Abgleich").
   */
  async flush(): Promise<void> {
    await Promise.all([...this.timers.keys()].map((param) => this.commit(param)));
  }

  private emit(): void {
    for (const fn of this.listeners) fn();
  }

  dispose(): void {
    for (const t of this.timers.values()) clearTimeout(t);
    this.timers.clear();
    this.target.clear();
  }
}
